import { toast } from 'sonner';
import i18next from 'i18next';
import { useAssetGroupMeasureApplicabilityStore } from '@/store/asset-group-measure-applicability.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class AssetGroupMeasureApplicabilityIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getErrorMessage(error, fallbackKey, fallbackDefault) {
        return getIntegrationErrorMessage(error);
    }

    _normalizeList(payload) {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    }

    async fetchAll(params = {}) {
        const store = useAssetGroupMeasureApplicabilityStore.getState();
        store.setLoading(true);
        store.clearError();

        try {
            const merged = { ...store.searchParams, ...params };
            const response = await this.service.getAll({
                page: merged.page,
                pageSize: merged.pageSize,
                search: merged.search || undefined,
                sortBy: merged.sortBy,
                sortOrder: merged.sortOrder,
                context: merged.context,
            });

            const rows = this._normalizeList(response.data);
            store.setItems(rows);
            if (response.pagination) {
                store.setPaginationData({
                    total: response.pagination.total ?? 0,
                    totalPages: response.pagination.totalPages ?? 1,
                    page: response.pagination.page ?? merged.page,
                    pageSize: response.pagination.pageSize ?? merged.pageSize,
                });
            }

            return { success: true, data: rows, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesMeasureApplicability:messages.fetchError',
                'Tedbir uygulanabilirliği listesi yüklenemedi',
            );
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchLinkedMeasures(id, params = {}) {
        const store = useAssetGroupMeasureApplicabilityStore.getState();
        store.setLinkedMeasureLoading(true);

        try {
            const merged = { ...store.linkedMeasureSearchParams, ...params };
            const response = await this.service.getLinkedMeasures(id, {
                page: merged.page,
                pageSize: merged.pageSize,
                search: merged.search || undefined,
                sortBy: merged.sortBy,
                sortOrder: merged.sortOrder,
                context: merged.context,
            });

            const rows = this._normalizeList(response.data);
            store.setLinkedMeasureItems(rows);
            if (response.pagination) {
                store.setLinkedMeasurePagination({
                    total: response.pagination.total ?? 0,
                    totalPages: response.pagination.totalPages ?? 1,
                    page: response.pagination.page ?? merged.page,
                    pageSize: response.pagination.pageSize ?? merged.pageSize,
                });
            }

            return { success: true, data: rows, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchLinkedMeasuresError',
                'İlişkili tedbir listesi yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLinkedMeasureLoading(false);
        }
    }

    async fetchDetail(id) {
        const store = useAssetGroupMeasureApplicabilityStore.getState();
        store.setDetailLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setDetail(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchDetailError',
                'Detay yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setDetailLoading(false);
        }
    }

    async getById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesMeasureApplicability:messages.fetchError',
                'Kayıt detayı alınamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async update(id, payload) {
        try {
            const response = await this.service.update(id, payload);
            toast.success(
                i18next.t('bigdesMeasureApplicability:messages.updateSuccess', {
                    defaultValue: 'Measure applicability updated',
                }),
            );
            useAssetGroupMeasureApplicabilityStore.getState().updateItemInList(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesMeasureApplicability:messages.updateError',
                'Güncelleme başarısız',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchMeasureEvaluation(applicabilityId, measureId) {
        try {
            const response = await this.service.getMeasureEvaluation(applicabilityId, measureId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchEvaluationError',
                'Tedbir değerlendirmesi yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async updateMeasureEvaluation(applicabilityId, measureId, payload) {
        try {
            const response = await this.service.updateMeasureEvaluation(applicabilityId, measureId, payload);
            toast.success(
                i18next.t('bigdesCurrentSituationAnalysis:messages.updateEvaluationSuccess', {
                    defaultValue: 'Measure evaluation saved',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.updateEvaluationError',
                'Tedbir değerlendirmesi kaydedilemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchPriorMeasureEvaluations(applicabilityId, measureId) {
        try {
            const response = await this.service.getPriorMeasureEvaluations(applicabilityId, measureId);
            return { success: true, data: this._normalizeList(response.data) };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchPriorEvaluationsError',
                'Önceki değerlendirme kayıtları yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchPriorMeasureEvaluationDetail(applicabilityId, measureId, sourceApplicabilityId) {
        try {
            const response = await this.service.getPriorMeasureEvaluationDetail(
                applicabilityId,
                measureId,
                sourceApplicabilityId,
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchPriorEvaluationDetailError',
                'Seçilen değerlendirme detayı yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchPriorEvaluationRoadMapWorkPackages(applicabilityId, measureId, sourceApplicabilityId, params = {}) {
        try {
            const response = await this.service.getPriorEvaluationRoadMapWorkPackages(
                applicabilityId,
                measureId,
                sourceApplicabilityId,
                params,
            );
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchPriorRoadMapError',
                'Önizleme iş paketleri yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchPriorEvaluationCompensatoryControls(applicabilityId, measureId, sourceApplicabilityId, params = {}) {
        try {
            const response = await this.service.getPriorEvaluationCompensatoryControls(
                applicabilityId,
                measureId,
                sourceApplicabilityId,
                params,
            );
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchPriorCompensatoryError',
                'Önizleme telafi edici kontroller yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async applyPriorEvaluationLinks(applicabilityId, measureId, { roadMapIds = [], compensatoryIds = [] } = {}) {
        const roadMapList = (roadMapIds || []).filter(Boolean);
        const compensatoryList = (compensatoryIds || []).filter(Boolean);

        if (!roadMapList.length && !compensatoryList.length) {
            return { success: true, data: null };
        }

        try {
            if (roadMapList.length) {
                const roadMapResult = await this.linkRoadMapWorkPackages(
                    applicabilityId,
                    measureId,
                    roadMapList,
                    { silent: true },
                );
                if (!roadMapResult.success) {
                    return roadMapResult;
                }
            }

            if (compensatoryList.length) {
                const compensatoryResult = await this.linkCompensatoryControls(
                    applicabilityId,
                    measureId,
                    compensatoryList,
                    { silent: true },
                );
                if (!compensatoryResult.success) {
                    return compensatoryResult;
                }
            }

            toast.success(
                i18next.t('bigdesCurrentSituationAnalysis:messages.applyPriorLinksSuccess', {
                    defaultValue: 'Links from the previous evaluation were applied',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.linkError',
                'Bağlantı kurulamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchLinkedRoadMapWorkPackages(applicabilityId, measureId, params = {}) {
        try {
            const response = await this.service.getLinkedRoadMapWorkPackages(applicabilityId, measureId, params);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchLinkedRoadMapError',
                'Bağlı iş paketleri yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchAvailableRoadMapWorkPackages(applicabilityId, measureId, params = {}) {
        try {
            const response = await this.service.getAvailableRoadMapWorkPackages(applicabilityId, measureId, params);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchAvailableRoadMapError',
                'İş paketi listesi yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async linkRoadMapWorkPackages(applicabilityId, measureId, ids, opts = {}) {
        try {
            const response = await this.service.linkRoadMapWorkPackages(applicabilityId, measureId, ids);
            if (!opts.silent) {
                toast.success(
                    i18next.t('bigdesCurrentSituationAnalysis:messages.linkSuccess', {
                        defaultValue: 'Records linked',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.linkError',
                'Bağlantı kurulamadı',
            );
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        }
    }

    async unlinkRoadMapWorkPackage(applicabilityId, measureId, workPackageId) {
        try {
            await this.service.unlinkRoadMapWorkPackage(applicabilityId, measureId, workPackageId);
            toast.success(
                i18next.t('bigdesCurrentSituationAnalysis:messages.unlinkSuccess', {
                    defaultValue: 'Link removed',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.unlinkError',
                'Bağlantı kaldırılamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async bulkUnlinkRoadMapWorkPackages(applicabilityId, measureId, ids) {
        try {
            await this.service.bulkUnlinkRoadMapWorkPackages(applicabilityId, measureId, ids);
            toast.success(
                i18next.t('bigdesCurrentSituationAnalysis:messages.bulkUnlinkSuccess', {
                    defaultValue: 'Selected links removed',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.unlinkError',
                'Bağlantı kaldırılamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchLinkedCompensatoryControls(applicabilityId, measureId, params = {}) {
        try {
            const response = await this.service.getLinkedCompensatoryControls(applicabilityId, measureId, params);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchLinkedCompensatoryError',
                'Bağlı telafi edici kontroller yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchAvailableCompensatoryControls(applicabilityId, measureId, params = {}) {
        try {
            const response = await this.service.getAvailableCompensatoryControls(applicabilityId, measureId, params);
            return { success: true, data: response.data, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.fetchAvailableCompensatoryError',
                'Telafi edici kontrol listesi yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async linkCompensatoryControls(applicabilityId, measureId, ids, opts = {}) {
        try {
            const response = await this.service.linkCompensatoryControls(applicabilityId, measureId, ids);
            if (!opts.silent) {
                toast.success(
                    i18next.t('bigdesCurrentSituationAnalysis:messages.linkSuccess', {
                        defaultValue: 'Records linked',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.linkError',
                'Bağlantı kurulamadı',
            );
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        }
    }

    async unlinkCompensatoryControl(applicabilityId, measureId, compensatoryControlId) {
        try {
            await this.service.unlinkCompensatoryControl(applicabilityId, measureId, compensatoryControlId);
            toast.success(
                i18next.t('bigdesCurrentSituationAnalysis:messages.unlinkSuccess', {
                    defaultValue: 'Link removed',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.unlinkError',
                'Bağlantı kaldırılamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async bulkUnlinkCompensatoryControls(applicabilityId, measureId, ids) {
        try {
            await this.service.bulkUnlinkCompensatoryControls(applicabilityId, measureId, ids);
            toast.success(
                i18next.t('bigdesCurrentSituationAnalysis:messages.bulkUnlinkSuccess', {
                    defaultValue: 'Selected links removed',
                }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesCurrentSituationAnalysis:messages.unlinkError',
                'Bağlantı kaldırılamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
